import * as React from 'react'
import { ReactComponent as IconRank } from '../../../assets/v3/icons/rank.svg'
import { formatThousands } from '../../../common/helpers/translations'
import YumButton from '../../YumButton/YumButton'
import { isRtl } from '../../../i18nInit'
import { GoalWithProgress } from '../../../services/api.types'
import { ActivityView } from '../../../views/Activity/store/ActivityReducer'
import { t } from 'ttag'
import { Box } from '@material-ui/core'

export interface YumRankButtonProps {
  /** The goal with progress data containing rank info */
  goal: GoalWithProgress
  /** Current activity view determines which rank/total to display */
  activityView: ActivityView | string
  /** Click handler for the button */
  onClick: () => void
}

/**
 * Button component that displays the user's rank in a challenge.
 * Shows different rank/total depending on the current activity view:
 * - INDIVIDUAL: personal rank / participants count
 * - GROUP: team rank / team total
 * - COUNTRY: country rank / country total
 * - DIVISION_CATEGORY_*: division category rank / total
 */
export const YumRankButton: React.FC<YumRankButtonProps> = ({ goal, activityView, onClick }) => {
  // Determine which rank and total to display based on activityView
  const { rank, total } = React.useMemo(() => {
    // Check if this is a DivisionCategory view (e.g., "DIVISION_CATEGORY_abc123")
    const isDivisionCategoryView = typeof activityView === 'string' && activityView.startsWith('DIVISION_CATEGORY_')

    if (isDivisionCategoryView) {
      // Extract category ID from the activity view string
      const categoryId = activityView.replace('DIVISION_CATEGORY_', '')

      // Parse divisionCategoryRanks from JSON string if needed
      let parsedRanks = goal.divisionCategoryRanks
      if (typeof parsedRanks === 'string') {
        try {
          parsedRanks = JSON.parse(parsedRanks)
        } catch (e) {
          parsedRanks = {}
        }
      }

      const categoryRank = parsedRanks?.[categoryId]

      return {
        rank: categoryRank?.rank ?? null,
        total: categoryRank?.from ?? null,
      }
    }

    // Handle standard activity views
    switch (activityView) {
      case ActivityView.GROUP:
        return {
          rank: goal.teamRank,
          total: goal.teamRankFrom,
        }

      case ActivityView.COUNTRY:
        return {
          rank: goal.countryRank,
          total: goal.countryRankFrom,
        }

      case ActivityView.INDIVIDUAL:
      default:
        // Default to individual rank
        return {
          rank: goal.rank,
          total: goal.participantsCount,
        }
    }
  }, [activityView, goal])

  // Format the rank display text
  const rankText = React.useMemo(() => {
    const rankPart = rank ? formatThousands(rank) + ' / ' : ''
    const totalPart = total ? formatThousands(total) : ''
    return rankPart + totalPart
  }, [rank, total])

  if (!rankText) return null

  // RTL-aware margin styles
  const buttonStyle = !isRtl ? { marginRight: '8px' } : { marginLeft: '8px' }
  const spanStyle = isRtl ? { marginRight: '8px' } : { marginLeft: '8px' }

  return (
    <YumButton size="small" variant="outlined" style={buttonStyle} onClick={onClick}>
      <IconRank />
      <span style={spanStyle}>
        <Box display={{ xs: 'none', md: 'inline' }}>{rank ? t`Rank` : t`Participants`}: </Box>
        {rankText}
      </span>
    </YumButton>
  )
}

export default YumRankButton
